import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { API } from '@/App';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [signupChartData, setSignupChartData] = useState([]);
  const [cumulativeChartData, setCumulativeChartData] = useState([]);
  
  // Question stats and reinitialization
  const [questionStats, setQuestionStats] = useState(null);
  const [reinitLoading, setReinitLoading] = useState(false);
  const [reinitResult, setReinitResult] = useState(null);
  
  // User management state
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Create-user modal state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ email: '', password: '', name: '', is_admin: false });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  
  // Promo code creation state
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: '100_percent',
    discount_value: 100,
    applies_to: ['basic'],
    description: '',
    max_uses: null
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      window.location.href = '/';
      return;
    }
    loadDashboardData();
    loadQuestionStats();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
      
      // Load signup data for charts
      await loadChartData();
      
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Admin dashboard error:', error);
      console.error('Error details:', error.response);
      setLoading(false);
      
      if (error.response?.status === 403) {
        setError('Admin access required. Your account does not have admin privileges.');
        setTimeout(() => window.location.href = '/dashboard', 2000);
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please login again.');
        setTimeout(() => window.location.href = '/', 2000);
      } else if (!error.response) {
        // Network error or endpoint not found
        const errorMsg = `Network error: Unable to reach ${API}/admin/dashboard. Please check if the backend is running.`;
        setError(errorMsg);
        console.error(errorMsg);
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
        setError('Error loading admin dashboard: ' + errorMsg);
      }
    }
  };

  const loadChartData = async () => {
    try {
      // Get signup data for last 30 days
      const response = await axios.get(`${API}/admin/analytics/signups?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const signupsData = response.data.signups;
      const baseUserCount = response.data.base_user_count || 0;
      
      // Process data for charts
      const dates = Object.keys(signupsData).sort();
      
      // Daily signups chart data
      const dailyData = dates.map(date => ({
        date: date.slice(5), // MM-DD format
        signups: signupsData[date].total
      }));
      
      // Cumulative signups chart data - starts from base count
      let cumulative = baseUserCount;
      const cumulativeData = dates.map(date => {
        cumulative += signupsData[date].total;
        return {
          date: date.slice(5), // MM-DD format
          total: cumulative
        };
      });
      
      setSignupChartData(dailyData);
      setCumulativeChartData(cumulativeData);
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (tierFilter) params.tier = tierFilter;
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserError(null);

    const { email, password } = createUserForm;
    if (!email.trim() || !password) {
      setCreateUserError('Email and password are required');
      return;
    }
    if (password.length < 6) {
      setCreateUserError('Password must be at least 6 characters');
      return;
    }

    setCreateUserLoading(true);
    try {
      await axios.post(`${API}/admin/users`, {
        email: email.trim(),
        password,
        name: createUserForm.name.trim() || null,
        subscription_tier: 'full',
        is_admin: createUserForm.is_admin,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCreateUser(false);
      setCreateUserForm({ email: '', password: '', name: '', is_admin: false });
      await loadUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCreateUserError(Array.isArray(detail) ? detail[0]?.msg : detail || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const grantPlus = async (email) => {
    if (!window.confirm(`Grant lifetime Plus access to ${email}?`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/users/grant-plus`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data?.message || `Lifetime Plus granted to ${email}`);
      await loadUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(`Failed: ${Array.isArray(detail) ? detail[0]?.msg : detail || err.message}`);
    }
  };

  const loadPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data.promo_codes);
    } catch (error) {
      console.error('Error loading promo codes:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const [signups, revenue, usage] = await Promise.all([
        axios.get(`${API}/admin/analytics/signups?days=30`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/revenue`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAnalytics({
        signups: signups.data.signups,
        revenue: revenue.data,
        usage: usage.data
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'promos') {
      loadPromoCodes();
    } else if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, searchQuery, tierFilter]);

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User updated successfully');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      alert('Error updating user: ' + error.message);
    }
  };

  const createPromoCode = async () => {
    try {
      await axios.post(`${API}/admin/promo-codes`, newPromo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Promo code created successfully');
      setShowPromoForm(false);
      setNewPromo({
        code: '',
        discount_type: '100_percent',
        discount_value: 100,
        applies_to: ['basic'],
        description: '',
        max_uses: null
      });
      loadPromoCodes();
    } catch (error) {
      alert('Error creating promo code: ' + error.response?.data?.detail || error.message);
    }
  };

  const togglePromoCode = async (code, active) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${code}?active=${!active}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadPromoCodes();
    } catch (error) {
      alert('Error updating promo code');
    }
  };

  const loadQuestionStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/question-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionStats(response.data);
    } catch (error) {
      console.error('Error loading question stats:', error);
    }
  };

  const reinitializeQuestions = async () => {
    if (!window.confirm('Are you sure you want to reinitialize all questions? This will reload all questions from the standardized JSON files. This may take several minutes.')) {
      return;
    }
    
    setReinitLoading(true);
    setReinitResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/reinitialize-questions`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000 // 5 minutes timeout
      });
      setReinitResult({ success: true, message: response.data.message });
      loadQuestionStats(); // Refresh stats after reinitialization
    } catch (error) {
      setReinitResult({ 
        success: false, 
        message: error.response?.data?.detail || error.message 
      });
    } finally {
      setReinitLoading(false);
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User deleted successfully');
      loadUsers();
    } catch (error) {
      alert('Error deleting user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete promo code "${code}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Promo code deleted successfully');
      loadPromoCodes();
    } catch (error) {
      alert('Error deleting promo code: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', padding: '2rem' }}>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', padding: '2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', maxWidth: '600px', textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>⚠️ Error</h2>
          <p style={{ color: '#333', marginBottom: '1.5rem', lineHeight: '1.6' }}>{error}</p>
          <Button onClick={() => window.location.href = '/dashboard'} style={{ background: '#667eea' }}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div style={{ padding: '2rem', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>🔐 Admin Dashboard</h1>
          <Button 
            onClick={() => window.location.href = '/dashboard'}
            style={{ background: 'white', color: '#667eea' }}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem 2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '1rem' }}>
          {['overview', 'users', 'promos', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === tab ? 'white' : 'transparent',
                color: activeTab === tab ? '#667eea' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardStats && (
          <div>
            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              <Card>
                <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#667eea', marginBottom: '0.5rem' }}>
                    {dashboardStats.total_users}
                  </h3>
                  <p style={{ color: '#666', fontSize: '1rem' }}>Total Users</p>
                  <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-around' }}>
                    <div>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>{dashboardStats.basic_users}</p>
                      <p style={{ fontSize: '0.875rem', color: '#666' }}>Basic</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>{dashboardStats.plus_users}</p>
                      <p style={{ fontSize: '0.875rem', color: '#666' }}>Plus</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.5rem' }}>
                    {dashboardStats.new_signups_week}
                  </h3>
                  <p style={{ color: '#666', fontSize: '1rem' }}>New Signups (7 days)</p>
                  <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
                    Today: <strong>{dashboardStats.new_signups_today}</strong>
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem' }}>
                    {dashboardStats.total_tests_completed}
                  </h3>
                  <p style={{ color: '#666', fontSize: '1rem' }}>Tests Completed</p>
                  <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
                    Today: <strong>{dashboardStats.tests_completed_today}</strong>
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f59e0b', marginBottom: '0.5rem' }}>
                    ${dashboardStats.total_revenue.toFixed(2)}
                  </h3>
                  <p style={{ color: '#666', fontSize: '1rem' }}>Total Revenue</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                    {dashboardStats.total_promo_uses}
                  </h3>
                  <p style={{ color: '#666', fontSize: '1rem' }}>Promo Code Uses</p>
                  <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
                    Active Codes: <strong>{dashboardStats.active_promo_codes}</strong>
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* System Tools Card */}
            <div style={{ marginBottom: '2rem' }}>
              <Card>
                <CardHeader>
                  <CardTitle>🔧 System Tools</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Question Stats */}
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Question Database</h4>
                      {questionStats ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <div>Test Questions:</div>
                          <div style={{ fontWeight: '600' }}>{questionStats.questions?.toLocaleString()}</div>
                          <div style={{ paddingLeft: '1rem', color: '#666' }}>↳ Reading & Writing:</div>
                          <div style={{ fontWeight: '600', color: '#666' }}>{questionStats.rw_questions?.toLocaleString()}</div>
                          <div style={{ paddingLeft: '1rem', color: '#666' }}>↳ Math:</div>
                          <div style={{ fontWeight: '600', color: '#666' }}>{questionStats.math_questions?.toLocaleString()}</div>
                          <div>Practice Problems:</div>
                          <div style={{ fontWeight: '600' }}>{questionStats.practice_non_concept?.toLocaleString()}</div>
                          <div>Concept Quizzes:</div>
                          <div style={{ fontWeight: '600' }}>{questionStats.concept_quiz?.toLocaleString()}</div>
                          <div>Short Quiz:</div>
                          <div style={{ fontWeight: '600' }}>{questionStats.quiz_questions?.toLocaleString() || 0}</div>
                          <div>Total Tests:</div>
                          <div style={{ fontWeight: '600' }}>{questionStats.tests}</div>
                        </div>
                      ) : (
                        <p style={{ color: '#666' }}>Loading stats...</p>
                      )}
                    </div>
                    
                    {/* Reinitialize Button */}
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Reinitialize Questions</h4>
                      <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                        Reload all questions from standardized JSON files. This will replace all existing questions in the database.
                      </p>
                      <Button
                        onClick={reinitializeQuestions}
                        disabled={reinitLoading}
                        style={{ 
                          background: reinitLoading ? '#9ca3af' : '#ef4444',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        data-testid="reinit-questions-btn"
                      >
                        {reinitLoading ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Reinitializing... (this may take 5+ minutes)
                          </>
                        ) : (
                          <>🔄 Reinitialize All Questions</>
                        )}
                      </Button>
                      
                      {reinitResult && (
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '0.75rem', 
                          borderRadius: '6px',
                          background: reinitResult.success ? '#d1fae5' : '#fee2e2',
                          color: reinitResult.success ? '#065f46' : '#991b1b',
                          fontSize: '0.9rem'
                        }}>
                          {reinitResult.success ? '✅' : '❌'} {reinitResult.message}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '1.5rem' }}>
              {/* Daily Signups Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily New Sign-ups (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  {signupChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={signupChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="signups" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          name="Daily Sign-ups"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      No signup data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Cumulative Users Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Cumulative User Growth (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  {cumulativeChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={cumulativeChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="total" 
                          stroke="#667eea" 
                          strokeWidth={2}
                          name="Total Users"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      No user growth data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <Card style={{ marginBottom: '1.5rem' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <Input
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <select
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value="">All Tiers</option>
                    <option value="basic">Basic</option>
                    <option value="plus">Plus</option>
                  </select>
                  <Button
                    onClick={() => { setCreateUserError(null); setShowCreateUser(true); }}
                    data-testid="admin-create-user-btn"
                    style={{ background: '#10b981' }}
                  >
                    ＋ New User
                  </Button>
                  <Button onClick={() => exportToCSV(users, 'users')}>
                    📥 Export CSV
                  </Button>
                </div>
                
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Tier</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Tests Available</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Tests Completed</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Joined</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem' }}>{user.name}</td>
                          <td style={{ padding: '0.75rem' }}>{user.email}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <Badge style={{ background: user.subscription_tier === 'plus' ? '#8b5cf6' : '#3b82f6' }}>
                              {user.subscription_tier}
                            </Badge>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>{user.tests_available}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>{user.tests_completed || 0}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                onClick={() => setSelectedUser(user)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                              >
                                Edit
                              </Button>
                              {user.subscription_tier !== 'plus' && (
                                <Button
                                  onClick={() => grantPlus(user.email)}
                                  data-testid={`grant-plus-btn-${user.id}`}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem', background: '#8b5cf6', color: 'white' }}
                                  title="Grant lifetime Plus access"
                                >
                                  ✦ Grant Plus
                                </Button>
                              )}
                              <Button
                                onClick={() => deleteUser(user.id, user.email)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem', background: '#ef4444', color: 'white' }}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promos' && (
          <div>
            <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
              <h2 style={{ color: 'white', fontSize: '1.5rem' }}>Promo Codes</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button onClick={() => exportToCSV(promoCodes, 'promo-codes')}>
                  📥 Export CSV
                </Button>
                <Button onClick={() => setShowPromoForm(!showPromoForm)} style={{ background: '#10b981' }}>
                  + Create Promo Code
                </Button>
              </div>
            </div>

            {showPromoForm && (
              <Card style={{ marginBottom: '1.5rem' }}>
                <CardHeader>
                  <CardTitle>Create New Promo Code</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <Input
                      placeholder="Code (e.g., SUMMER2024)"
                      value={newPromo.code}
                      onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                    />
                    <Input
                      placeholder="Description"
                      value={newPromo.description}
                      onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                    />
                    <select
                      value={newPromo.applies_to[0]}
                      onChange={(e) => setNewPromo({...newPromo, applies_to: [e.target.value]})}
                      style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    >
                      <option value="basic">Basic Package</option>
                      <option value="plus">Plus Package</option>
                    </select>
                    <Input
                      type="number"
                      placeholder="Max uses (leave empty for unlimited)"
                      value={newPromo.max_uses || ''}
                      onChange={(e) => setNewPromo({...newPromo, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                    />
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <Button onClick={createPromoCode} style={{ flex: 1, background: '#10b981' }}>
                        Create
                      </Button>
                      <Button onClick={() => setShowPromoForm(false)} style={{ flex: 1, background: '#6b7280' }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Code</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Description</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Applies To</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Uses</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map(promo => (
                        <tr key={promo.code} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem', fontWeight: '600' }}>{promo.code}</td>
                          <td style={{ padding: '0.75rem' }}>{promo.description}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <Badge>{promo.applies_to?.join(', ') || promo.tier}</Badge>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            {promo.uses} {promo.max_uses ? `/ ${promo.max_uses}` : ''}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <Badge style={{ background: promo.active ? '#10b981' : '#ef4444' }}>
                              {promo.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              <Button
                                onClick={() => togglePromoCode(promo.code, promo.active)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                              >
                                {promo.active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                onClick={() => deletePromoCode(promo.code)}
                                style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem', background: '#ef4444', color: 'white' }}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <Card>
              <CardHeader>
                <CardTitle>Revenue Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Total Revenue</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>${analytics.revenue.total_revenue.toFixed(2)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Basic Revenue</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>${analytics.revenue.basic_revenue.toFixed(2)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Plus Revenue</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>${analytics.revenue.plus_revenue.toFixed(2)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Total Transactions</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{analytics.revenue.total_transactions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Usage Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Total Completions</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{analytics.usage.total_completions}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#666' }}>Average Score</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{analytics.usage.average_score}</p>
                  </div>
                </div>
                
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>Most Popular Tests</h4>
                  {Object.entries(analytics.usage.popular_tests).slice(0, 5).map(([testId, count]) => (
                    <div key={testId} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e5e7eb' }}>
                      <span>{testId}</span>
                      <span style={{ fontWeight: '600' }}>{count} completions</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <Card style={{ maxWidth: '500px', width: '90%' }}>
            <CardHeader>
              <CardTitle>Edit User: {selectedUser.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Name</label>
                  <Input
                    value={selectedUser.name}
                    onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Tier</label>
                  <select
                    value={selectedUser.subscription_tier}
                    onChange={(e) => setSelectedUser({...selectedUser, subscription_tier: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value="basic">Basic</option>
                    <option value="plus">Plus</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Tests Available</label>
                  <Input
                    type="number"
                    value={selectedUser.tests_available}
                    onChange={(e) => setSelectedUser({...selectedUser, tests_available: parseInt(e.target.value)})}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button
                    onClick={() => updateUser(selectedUser.id, {
                      name: selectedUser.name,
                      tier: selectedUser.subscription_tier,
                      tests_available: selectedUser.tests_available
                    })}
                    style={{ flex: 1, background: '#10b981' }}
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => setSelectedUser(null)}
                    style={{ flex: 1, background: '#6b7280' }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="admin-create-user-modal"
          onClick={() => !createUserLoading && setShowCreateUser(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
        >
          <Card
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(95vw, 460px)', maxHeight: '92vh', overflowY: 'auto' }}
          >
            <CardHeader>
              <CardTitle>Create New User</CardTitle>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.35rem 0 0 0' }}>
                The user will be able to log in immediately with the credentials below.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div>
                  <label htmlFor="cu-email" style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                    Email <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <Input
                    id="cu-email"
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                    placeholder="user@example.com"
                    data-testid="create-user-email"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="cu-password" style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                    Password <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <Input
                    id="cu-password"
                    type="text"
                    value={createUserForm.password}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                    placeholder="Min 6 characters"
                    data-testid="create-user-password"
                    required
                    minLength={6}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                    Visible so you can copy it to email to the user.
                  </p>
                </div>
                <div>
                  <label htmlFor="cu-name" style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
                    Name <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <Input
                    id="cu-name"
                    type="text"
                    value={createUserForm.name}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                    placeholder="Defaults to email prefix"
                    data-testid="create-user-name"
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={createUserForm.is_admin}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, is_admin: e.target.checked })}
                    data-testid="create-user-is-admin"
                  />
                  Grant admin access
                </label>

                {createUserError && (
                  <div
                    role="alert"
                    data-testid="create-user-error"
                    style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '0.55rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    {createUserError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
                  <Button
                    type="button"
                    onClick={() => setShowCreateUser(false)}
                    disabled={createUserLoading}
                    style={{ background: '#e5e7eb', color: '#111827' }}
                    data-testid="create-user-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createUserLoading}
                    data-testid="create-user-submit"
                  >
                    {createUserLoading ? 'Creating...' : 'Create User'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
